import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getConfigTenants } from "@/Api/configTenant";
import { ApiKeyMaps, loadGoogleMapsScript } from "@/components/GoogleMap";

export type GeofenceMapRef = {
  flyTo: (options: { center: [number, number]; zoom?: number }) => void;
};

type GeofenceMapProps = {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  zoom?: number;
  // Quando true: clique no mapa e arrasto do marcador ajustam o ponto central
  editable?: boolean;
  onPick?: (lng: number, lat: number) => void;
};

// Chave do Google Maps configurada no tenant (Configurações > Mapas); cai na
// chave padrão do sistema se a config não existir ou a requisição falhar.
export const resolveApiKey = async (): Promise<string> => {
  try {
    const { data, ok } = await getConfigTenants();
    if (ok && data?.response) {
      const config = (data.response as Array<{ key: string; value: string }>).find(
        (c) => c.key === "google_maps_api_key"
      );
      if (config?.value) return config.value;
    }
  } catch (error) {
    console.error("Error fetching Google Maps key:", error);
  }

  return ApiKeyMaps;
};

export const GeofenceMap = forwardRef<GeofenceMapRef, GeofenceMapProps>(
  (
    { latitude, longitude, radiusMeters, zoom = 15, editable = false, onPick },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const circleRef = useRef<google.maps.Circle | null>(null);
    const onPickRef = useRef(onPick);
    onPickRef.current = onPick;

    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      flyTo: ({ center, zoom: flyZoom }) => {
        const map = mapRef.current;
        if (!map) return;
        map.panTo({ lat: center[1], lng: center[0] });
        if (flyZoom) map.setZoom(flyZoom);
      },
    }));

    useEffect(() => {
      let cancelled = false;
      let listeners: google.maps.MapsEventListener[] = [];

      const init = async () => {
        try {
          const apiKey = await resolveApiKey();
          await loadGoogleMapsScript(apiKey);
          if (cancelled || !containerRef.current) return;

          const center = { lat: latitude, lng: longitude };

          const map = new google.maps.Map(containerRef.current, {
            center,
            zoom,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            clickableIcons: false,
          });

          const marker = new google.maps.Marker({
            position: center,
            map,
            draggable: editable,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#2563eb",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });

          const circle = new google.maps.Circle({
            map,
            center,
            radius: radiusMeters,
            fillColor: "#2563eb",
            fillOpacity: 0.35,
            strokeColor: "#1d4ed8",
            strokeWeight: 3,
            clickable: false,
          });

          if (editable) {
            listeners = [
              map.addListener("click", (e: google.maps.MapMouseEvent) => {
                if (e.latLng) {
                  onPickRef.current?.(e.latLng.lng(), e.latLng.lat());
                }
              }),
              marker.addListener("drag", (e: google.maps.MapMouseEvent) => {
                if (e.latLng) {
                  onPickRef.current?.(e.latLng.lng(), e.latLng.lat());
                }
              }),
            ];
          }

          mapRef.current = map;
          markerRef.current = marker;
          circleRef.current = circle;
          setIsReady(true);
        } catch (err) {
          console.error("GeofenceMap: erro ao carregar o Google Maps", err);
          setError("Não foi possível carregar o Google Maps");
        }
      };

      init();

      return () => {
        cancelled = true;
        listeners.forEach((listener) => listener.remove());
        markerRef.current?.setMap(null);
        circleRef.current?.setMap(null);
        markerRef.current = null;
        circleRef.current = null;
        mapRef.current = null;
      };
      // Mapa é criado uma única vez; posição e raio são sincronizados abaixo
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const map = mapRef.current;
      const marker = markerRef.current;
      const circle = circleRef.current;
      if (!isReady || !map || !marker || !circle) return;

      const position = { lat: latitude, lng: longitude };
      marker.setPosition(position);
      circle.setCenter(position);
      circle.setRadius(radiusMeters);

      // Só recentraliza se o ponto saiu da área visível (não "pula" a cada clique)
      const bounds = map.getBounds();
      if (bounds && !bounds.contains(position)) {
        map.panTo(position);
      }
    }, [isReady, latitude, longitude, radiusMeters]);

    if (error) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-red-50 p-4 text-center text-sm text-red-600">
          {error}
        </div>
      );
    }

    return (
      <div className="relative h-full w-full" style={{ minHeight: "inherit" }}>
        <div ref={containerRef} className="absolute inset-0" />
      </div>
    );
  }
);

GeofenceMap.displayName = "GeofenceMap";
